import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import useQuery from '../utils/useQuery';
import { validateLength } from '../utils/validation';
import { sendPermTokenRequest, sendVerifyEmailRequest, sendSetPasswordRequest } from '../utils/api/accountApi';
import { sendProfileRequest } from '../utils/api/profileApi';
import { trackSetPassword } from '../services/analytics/analyticsProvider';
import ProfileLayout from '../components/layout/ProfileLayout';
import PasswordInput from '../components/Enter/fields/PasswordInput';
import SubmitButton from '../components/Enter/fields/SubmitButton';
import { genericErrorMessage } from '../utils/lang/commonErrors';
import successIcon from '../../assets/icons/success-with-grad.svg';
import styles from './SetPassword.scss';

/*
Don't confuse this route with /password/set
This one is part of the web funnel
 */
const SetPasswordRoute = () => {
  const { email, permanent_access_token: permanentToken, 'verify-email': verifyEmail } = useQuery();

  useEffect(() => {
    if (verifyEmail && permanentToken) {
      sendVerifyEmailRequest(permanentToken);
    }
  }, []);

  const history = useHistory();

  const [password, setPassword] = useState('');
  const onPasswordChange = (event) => setPassword(event.target.value);

  const [isLoading, setLoading] = useState(false);

  const [formErrors, setFormErrors] = useState({ pass: '' });

  const intl = useIntl();

  const onSubmit = () => {
    setLoading(true);
    const passError = validateLength(password, 8, 21);

    if (passError) {
      setFormErrors({
        pass: passError,
      });
      setLoading(false);
    } else {
      sendPermTokenRequest(permanentToken)
        .then(
          () => sendProfileRequest(false, { shouldDropToLogin: false }),
          () => {},
        )
        .then(
          (data) => data?.profile,
          () => {},
        )
        .then((profile) =>
          Promise.allSettled([
            sendSetPasswordRequest(password, { shouldDropToLogin: false }),
            trackSetPassword(profile),
          ]),
        )
        .then((results) => {
          if (results[0].status === 'fulfilled') {
            history.push('/profile?registration_source=web_funnel');
          } else {
            setFormErrors({
              pass: intl.formatMessage(genericErrorMessage),
            });
            setLoading(false);
          }
        });
    }
  };

  return (
    <ProfileLayout withConfetti>
      <div className={styles.centerWrapper}>
        <article className={styles.wrapper}>
          <img src={successIcon} alt="" width="36" height="36" />
          <h1 className={styles.title}>
            <FormattedMessage
              id="newSetPassword.title"
              defaultMessage="Thanks for choosing ClearVPN!"
              description="Title of the page, where user completes sign up by setting password"
            />
          </h1>
          <p className={styles.subtitle}>
            <FormattedMessage
              id="newSetPassword.subtitle"
              description="Subtitle of the page, where user completes sign up by setting password"
              defaultMessage={
                'Yay, your payment was successful.' +
                '\n' +
                'To fully embrace your new online experience with ClearVPN Premium, please, complete account creation by setting a password.'
              }
            />
          </p>

          <section className={styles.card}>
            <div className={styles.glowBackground} />

            <h2 className={styles.cardTitle}>
              <FormattedMessage
                id="newSetPassword.form.title"
                defaultMessage="Complete your account creation"
                description="Title of the form, where user completes sign up by setting password"
              />
            </h2>
            {email ? <span className={styles.email}>{email}</span> : null}

            {}
            <label className={styles.createPassword}>
              <FormattedMessage
                id="newSetPassword.form.passwordLabel"
                defaultMessage="Set password"
                description="Label of the password field within a Set Password form"
              />
            </label>
            <PasswordInput passValue={password} label="" error={formErrors.pass} onChange={onPasswordChange} />

            <div className={styles.buttonWrapper}>
              <SubmitButton
                isLoading={isLoading}
                text={intl.formatMessage({
                  id: 'newSetPassword.form.submit',
                  defaultMessage: 'Create Account',
                  description: 'Submit button, creates a user account with the provided password',
                })}
                onSubmit={onSubmit}
              />
            </div>
          </section>
        </article>
      </div>
    </ProfileLayout>
  );
};

export default SetPasswordRoute;
